import { BBox, FeatureCollection, Geometry } from "geojson";
import { Model, SceneFeature } from "../types/scenes";

export function createScene(sceneID: string, date: string) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });
  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/`, {
    method: "POST",
    headers,
    body: JSON.stringify({ s3_prefix: sceneID, date })
  })
    .then(r => {
      if (!r.ok) {
        if (r.status === 409) throw new Error("A scene for these files already exists");
        throw new Error("Unable to create scene.");
      }
      return r.json();
    });
}

export function getSignedUrl(sceneID: string, filename: string, signal: AbortSignal) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });
  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneID}/upload/${filename}`, {
    method: "GET",
    headers,
    signal
  })
    .then(r => {
      if (!r.ok) throw new Error(`Unable to get signed URL for file ${filename}`);
      return r.json();
    });
}

export function uploadFile(url: string, file: File, signal: AbortSignal) {
  return fetch(url, {
    method: "PUT",
    signal,
    body: file
  });
}

export function ingest(sceneId: string, model: Model): Promise<Response> {
  const { id, name, version } = model;
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });

  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneId}/run_model`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      skip_pansharpening: false,
      model_id: id,
      model_name: name,
      model_version: version
    })
  })
    .then(r => {
      if (!r.ok) throw new Error(`Unable to start ingestion for scene ${sceneId} and model ${model}.`);
      return r.json();
    });
}

export type GetScenesQuery = {
  selectedDates: Date[];
  bbox: BBox | undefined;
  subResource: "user" | ""| undefined;
}

export function getScenes({
  selectedDates,
  bbox,
  subResource = "",
}: GetScenesQuery): Promise<FeatureCollection<Geometry, SceneFeature>> {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });

  const queryParams = [
    ["start_date", selectedDates[0].toISOString().split("T")[0]],
    ["end_date", selectedDates[1].toISOString().split("T")[0]]
  ];
  bbox && queryParams.push(["bbox", bbox.join(",")]);

  const query = new URLSearchParams(queryParams);

  return fetch(
    `${process.env.REACT_APP_API_BASE}/scenes/${subResource}?${query.toString()}`,
    { headers, }
  )
    .then(r => {
      if (!r.ok) throw new Error("Unable to start get scenes.");
      return r.json();
    });
}

export function getScene(sceneId: string) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });

  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneId}`, {
    headers,
  })
    .then(r => {
      if (!r.ok) throw new Error("Unable to start get scene.");
      return r.json();
    });
}

type Band = {
  max: number;
};
type Bands = {
  [key: string]: Band
}

const defaultBandValues = {
  maxB2: 250,
  maxB3: 250,
  maxB5: 250
};

export async function getTileBandMaxValues(id: string) {
  try {
    const token = localStorage.getItem("token");
    const headers = new Headers({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    });
    const requestUrl = `${process.env.REACT_APP_API_BASE}/tiler/statistics?url=${id}/pansharpened_cog.tif`;
    const response = await fetch(requestUrl, { headers });
    if (!response.ok) throw new Error("Unable to get scene thumbnail.");
    const bands: Bands = await response.json();
    const {
      b2: { max: maxB2 },
      b3: { max: maxB3 },
      b5: { max: maxB5 },
    } = bands;
    return { maxB2, maxB3, maxB5 };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching and processing tile statistics:", error);
    return defaultBandValues;
  }
}


export function getScenePreview(sceneId: string, bbox: string | undefined, width: number, height: number, brightness = 1.25, contrast = 4) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });
  const bboxOrPreview = bbox && bbox.length > 0 ? `/bbox/${bbox}/${width}x${height}.png` : "preview";
  
  // Prepare the URL and fetch data using the max values
  return getTileBandMaxValues(sceneId).then(({ maxB2, maxB3 }) => {
    const s3Url = `${sceneId}/pansharpened_cog.tif`;
    const requestUrl = `${process.env.REACT_APP_API_BASE}/tiler/${bboxOrPreview}?url=${s3Url}&coord_crs=epsg:4326&dst_crs=epsg:3857&bidx=5&rescale=0,${maxB2}&bidx=3&rescale=0,${maxB3}&bidx=2&rescale=0,${maxB2}&color_formula=Gamma RGB ${brightness} Saturation 1.1 Sigmoidal RGB ${contrast} 0.5`;

    return fetch(requestUrl, { headers })
      .then(r => {
        if (!r.ok) throw new Error("Unable to get scene thumbnail.");
        return r;
      });
  });
}

export function deleteScene(sceneID: string) {
  const token = localStorage.getItem("token");
  const headers = new Headers({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  });
  return fetch(`${process.env.REACT_APP_API_BASE}/scenes/${sceneID}`, {
    method: "DELETE",
    headers,
  })
    .then(r => {
      if (!r.ok) {
        throw new Error("Unable to delete scene.");
      }
      return r.json();
    });
}
